"use client";

import * as React from "react";
import { cn } from "../lib/cn";
import { AuthBackground } from "./AuthBackground";
import { FlickeringGrid } from "./FlickeringGrid";

/**
 * AuthShell - the full-page stage the auth ROUTE mounts (login / signup /
 * forgot-password / reset-password). Distinct from `AuthModal`, which is the
 * dismissible overlay (focus-trap + floating labels). Both coexist: reach for
 * `AuthModal` to interrupt a logged-out user mid-flow, and `AuthShell` for the
 * dedicated `/login`-style page.
 *
 * Reproduces the scaffold the unified AuthScreen / ForgotPassword /
 * ResetPassword each copy-pasted: a `fixed inset-0 z-[300]` viewport stage on
 * `bg-background`, a cheap CSS fade-in, and a safe-centered, scrollable column.
 * `[justify-content:safe_center]` matters - it keeps a tall card scrollable
 * from the top on short viewports instead of clipping its header.
 *
 * Background slot (default `"logo-mask"`):
 *   - `"logo-mask"` - the logo-clipped flickering diamond (`AuthBackground`),
 *     bleeding off each edge and vertically locked to the card. This is the one
 *     the unified app uses.
 *   - `"grid"`      - the full-area `FlickeringGrid` field (re-tints per theme).
 *   - `"none"`      - flat `bg-background`, no canvas.
 *   - any `ReactNode` - your own backdrop, rendered behind the column.
 *
 * Animation: the stage fade is plain CSS via `tailwindcss-animate`
 * (`animate-in fade-in-0`), so the expensive spring lives only on the card
 * (`AuthCard`). `tailwindcss-animate` already drops the animation under
 * `prefers-reduced-motion`, and the background honors it on its own (static
 * frame). Pass a single `AuthCard` (or any node) as `children`.
 *
 *   <AuthShell>
 *     <AuthCard logoSrc="/logo-blue.png">…</AuthCard>
 *   </AuthShell>
 */
export interface AuthShellProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Backdrop behind the centered column. Default `"logo-mask"`. */
  background?: "logo-mask" | "grid" | "none" | React.ReactNode;
  /** Logo mark URL forwarded to the `"logo-mask"` background. */
  logoSrc?: string;
  /** Extra classes on the safe-centered scroll column (the card's container). */
  columnClassName?: string;
}

const isBackgroundKeyword = (
  value: AuthShellProps["background"],
): value is "logo-mask" | "grid" | "none" =>
  value === "logo-mask" || value === "grid" || value === "none";

export const AuthShell = React.forwardRef<HTMLDivElement, AuthShellProps>(
  ({ className, background = "logo-mask", logoSrc, columnClassName, children, ...props }, ref) => {
    let backdrop: React.ReactNode = null;
    if (isBackgroundKeyword(background)) {
      if (background === "logo-mask") {
        backdrop = <AuthBackground logoSrc={logoSrc} />;
      } else if (background === "grid") {
        backdrop = <FlickeringGrid className="absolute inset-0" maxOpacity={0.18} />;
      }
      // "none" - no backdrop.
    } else {
      backdrop = background;
    }

    return (
      <div
        ref={ref}
        data-auth-stage
        data-slot="auth-shell"
        className={cn(
          "fixed inset-0 z-[300] flex flex-col overflow-hidden bg-background",
          "animate-in fade-in-0 duration-240 motion-reduce:animate-none",
          className,
        )}
        {...props}
      >
        {backdrop}
        <div className="relative grid min-h-0 flex-auto grid-cols-1 place-items-center bg-transparent">
          <div
            className={cn(
              "relative flex h-full min-h-0 w-full min-w-0 flex-col items-center overflow-y-auto px-7 py-8 [justify-content:safe_center]",
              columnClassName,
            )}
          >
            {children}
          </div>
        </div>
      </div>
    );
  },
);
AuthShell.displayName = "AuthShell";
